import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const InfluencerMarketingROICalculator = () => {
  const [investment, setInvestment] = useState(10000);
  const [revenue, setRevenue] = useState(25000);
  const [newFollowers, setNewFollowers] = useState(5000);
  const [roi, setRoi] = useState(0);

  useEffect(() => {
    calculateROI();
  }, [investment, revenue]);

  const calculateROI = () => {
    const profit = revenue - investment;
    const calculatedRoi = (profit / investment) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/influencer-marketing-roi-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Influencer Marketing ROI Calculator: Measure Campaign Success | Free Tool</title>
        <meta name="description" content="Calculate your Influencer Marketing ROI instantly. Compare with industry benchmarks, get optimization tips, and improve campaign performance." />
        <meta name="keywords" content="influencer marketing ROI calculator, influencer metrics, campaign performance, creator partnerships, influencer analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Influencer Marketing ROI Calculator: Optimize Campaign Returns" />
        <meta property="og:description" content="Calculate your Influencer Marketing ROI instantly. Get actionable insights to improve campaign performance and boost returns." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Influencer Marketing ROI Calculator: Optimize Campaign Returns" />
        <meta property="twitter:description" content="Calculate your Influencer Marketing ROI instantly. Get actionable insights to improve campaign performance and boost returns." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Influencer Marketing ROI Calculator",
            "description": "Free calculator to measure and optimize influencer marketing campaign returns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Influencer ROI",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Influencer Marketing ROI",
              "description": "The return on investment from influencer marketing campaigns, measuring costs against revenue and follower growth."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Brand Managers, Marketing Directors, Social Media Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Influencer Marketing ROI",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total investment"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input revenue generated"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add new followers gained"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your ROI and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Influencer Marketing ROI Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Investment</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="25000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Followers Gained</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={newFollowers}
                  onChange={(e) => setNewFollowers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {roi}%
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Influencer Marketing ROI</p>
          <p className="text-gray-600 mt-4">New Followers: {newFollowers}</p>
          <p className="text-gray-600">Cost per New Follower: ${(investment / newFollowers).toFixed(2)}</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Influencer Marketing ROI is {roi}%. This means that for every dollar invested, you're generating ${(roi / 100 + 1).toFixed(2)} in return, while gaining {newFollowers} new followers at ${(investment / newFollowers).toFixed(2)} per follower.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">ROI Benchmarks by Influencer Type</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Mega-Influencers: 300-500%</li>
          <li>Macro-Influencers: 400-600%</li>
          <li>Mid-Tier Influencers: 500-700%</li>
          <li>Micro-Influencers: 600-800%</li>
          <li>Nano-Influencers: 700-900%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Campaign Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Choose aligned influencers</li>
          <li>Set clear goals</li>
          <li>Track conversions</li>
          <li>Build long-term relationships</li>
          <li>Encourage authentic content</li>
          <li>Leverage UGC</li>
          <li>Analyze performance</li>
          <li>Consider micro-influencers</li>
          <li>Test different formats</li>
          <li>Monitor engagement</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Performance Metrics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Engagement Rate</li>
          <li>Conversion Rate</li>
          <li>Brand Mentions</li>
          <li>Follower Growth</li>
          <li>Content Quality</li>
          <li>Audience Sentiment</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Proper influencer fit: +60% ROI</li>
          <li>Long-term partnerships: +45% ROI</li>
          <li>Multi-platform campaigns: +35% ROI</li>
          <li>UGC integration: +40% ROI</li>
          <li>Performance tracking: +30% ROI</li>
        </ul>
      </div>
    </div>
  );
};

export default InfluencerMarketingROICalculator;
